import { ChevronDownIcon, ChevronRightIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box, Button, Checkbox, CheckboxGroup, Divider, Flex,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
  MenuItem,
  Collapse,
  RadioGroup,
  Radio,
  Stack,
  Card,
  CardBody
} from '@chakra-ui/react';
import CustomDeepLinkInput from 'components/genericForms/CustomDeepLinkInput';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useCreateAdspressoCreativeMutation } from 'store/adspresso/adspressoAdApi';
import { useGetAdvertisableAppsByIdQuery } from 'store/campaigns/adsetFormApi';
import {
  call_to_action_options
} from 'utils/callToActions';
import { formatDateTime, removeEmptyValues, truncateString } from 'utils/functions';
import * as Yup from 'yup';
import BuildUrlParams from './BuildUrlParams';
import UrlParameter from './UrlParameter';
import CatalogueFrame from 'components/genericForms/CatalogueFrame';

function CatalogueMediaCreative({ adsAccounts, adFormik, adsetsListData, campaignId, adData }) {

  const toast = useToast();
  const backgroundColor = useColorModeValue('gray.50', 'gray.800');
  const inputBackground = useColorModeValue('#F5F3F9', '#1e1530');
  const [selectedAdAccounts, setSelectedAdAccounts] = useState([]);
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const [isUrlModalOpen, setUrlModalOpen] = useState(false);
  const [isUrlParamModalOpen, setUrlParamModalOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [expandedUrlTagSections, setExpandedUrlTagSections] = useState({});
  const { isOpen: isCreativeModalOpen, onOpen: onCreativeModalOpen, onClose: onCreativeModalClose } = useDisclosure();

  const [createAdspressoAds, { isLoading: isAdsCreating, error: createError }] = useCreateAdspressoCreativeMutation();

  const adsAccountIds =  adData?.adsAccounts.map(account => account?.adsAccountId);

  const { data: advertisableApps } = useGetAdvertisableAppsByIdQuery({
    adsAccountIds
  }, {
    skip: !adsAccountIds
  });

// Handler just updates selectedAdAccounts
const handleAdAccountChange = (selectedValues) => {
  const selectedAccounts = adsAccounts.filter(account =>
    selectedValues.includes(account.adsAccountId)
  );
  setSelectedAdAccounts(selectedAccounts);
};

useEffect(() => {
  if (selectedAdAccounts.length === 0) return;

  const prevLinks = formik.values.links || [];
  const prevUrlTags = formik.values.url_tags || [];
  const prevTemplateSpecs = formik.values.template_url_spec || [];

  const updatedLinks = selectedAdAccounts.map(account => {
    const existing = prevLinks.find(l => l.adsAccountId === account.adsAccountId);
    return {
      adsAccountId: account.adsAccountId,
      link: existing?.link || '',
      caption: existing?.caption || '', 
    };
  });

  const updatedUrlTags = selectedAdAccounts.map(account => {
    const existing = prevUrlTags.find(l => l.adsAccountId === account.adsAccountId);
    return {
      adsAccountId: account.adsAccountId,
      url_tags: existing?.url_tags || '',
    };
  });

  const updatedTemplateSpecs = selectedAdAccounts.map(account => {
    const existing = prevTemplateSpecs.find(spec => spec.adsAccountId === account.adsAccountId);
    return {
      adsAccountId: account.adsAccountId,
      webUrl: existing?.webUrl || '',
      androidUrl: existing?.androidUrl || '',
      iosUrl: existing?.iosUrl || '',
      app_id: existing?.app_id || '',
      override_catalog: existing?.override_catalog || false,
    };
  });

  formik.setFieldValue('links', updatedLinks);
  formik.setFieldValue('url_tags', updatedUrlTags);
  formik.setFieldValue('template_url_spec', updatedTemplateSpecs);
}, [selectedAdAccounts]);


  const mustStartWithHttpRegex = /^https?:\/\/.+$/;

  const deepLinkSchema = Yup.object().shape({
    iosUrl: Yup.string()
        .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://'),
    androidUrl: Yup.string()
        .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://'),
    webUrl: Yup.string()
        .matches(mustStartWithHttpRegex, 'Must be a valid URL starting with http:// or https://'),
  });

  const linkEntrySchema = Yup.object().shape({
    link: Yup.string()
      .url('Must be a valid website URL')
      .required('Url is required'),
  });

  const schema = Yup.object().shape({
    links: Yup.array()
      .of(linkEntrySchema)
      .required('Links are required')
      .min(1, 'At least one website URL is required'),
    template_url_spec: Yup.array().of(deepLinkSchema),
    adSets: Yup.object()
    .test(
      'at-least-one-adset',
      'Please select at least one ad set',
      (value) =>
        value &&
        Object.values(value).some((adSets) => Array.isArray(adSets) && adSets.length > 0)
    ),
  });
  
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      message: '',
      name: '',
      description: '',
      type: 'SHOP_NOW',
      applyForAll: false,
      links: [],
      adSets: {},
      applink_treatment: 'web_only',
      template_url_spec: [
        {
          adsAccountId: '',
          webUrl: '',
          iosUrl: '',
          androidUrl: '',
          app_id: '',
          override_catalog: false
        }
      ],
      url_tags: [],
      image_layer_specs: [],
      format_option: 'carousel_images_multi_items',
    },
    validationSchema: schema,
    onSubmit: (values, { resetForm }) => {

      const { name, message, description, type, links, url_tags, applink_treatment, template_url_spec, adSets,  image_layer_specs, format_option} = values;

      const urlTags = url_tags.filter(account => account.url_tags?.trim());

      const adsAccounts = selectedAdAccounts?.map(account => ({
        id: account?.id,
        adsAccountId: account?.adsAccountId,
        adsAccountName: account?.adsAccountName,
        businessName: account?.businessName,
        businessId: account?.businessId,
      }));

      // Step 1: Flatten selected ad sets
      const adsets = adsAccounts.flatMap(account => adSets[account.adsAccountId] || []);

      // Step 2: Deduplicate by `_id`
      const uniqueAdsets = adsets.filter(
        (item, index, self) => index === self.findIndex(i => i._id === item._id)
      );

      const fileName = `Ad_${formatDateTime()}`;
      const productSetId = adsetsListData?.data[0]?.promoted_object[0]?.product_set_id || "";

      const adcreatives = selectedAdAccounts?.map(account => {
        const spec = image_layer_specs?.find(s => s.adsAccountId === account.adsAccountId);
      
        const image_layer_specs_array = spec?.layers
          ? Object.values(spec.layers).map(layer => {
              const { frame_name, frame_url, ...filteredLayer } = layer;
              return filteredLayer;
            })
          : undefined;
      
        const isCarousel = adFormik?.values?.format === 'CAROUSEL';
        const isSingle = adFormik?.values?.format === 'SINGLE';
        const isMultiItemCarousel = format_option === 'carousel_images_multi_items';
      
        return {
          adsAccountId: account.adsAccountId,
          product_set_id: productSetId,
          ...(isSingle && { fileName }),
          ...(isCarousel && { format_option }),
          ...(
            (isSingle || (isCarousel && isMultiItemCarousel)) &&
            image_layer_specs_array && { image_layer_specs: image_layer_specs_array }
          ),
        };
      });
      

      const messagePayload =  {
              asset_feed_spec: selectedAdAccounts?.map(account => ({
                adsAccountId: account.adsAccountId,
                ad_formats: ['CAROUSEL', 'COLLECTION'],
                optimization_type: 'FORMAT_AUTOMATION',
              })),
            };

      const intialPayload = {
        adType: adFormik.values.format,
        contextual_multi_ads: {
          enroll_status: adFormik.values.enroll_status,
        },
        campaign: campaignId,
        adsets: uniqueAdsets,
        ...(adFormik?.values?.format === 'CAROUSEL' && ({  ...messagePayload })), 
        name,
        message,
        ...(adFormik?.values?.format === 'CAROUSEL' && ({ fileName: `Ad_${formatDateTime()}` })), 
        description,
        type: type,
        links: adFormik?.values?.format === 'SINGLE' ? links?.map(({caption, ...rest}) => rest) : links,
        applink_treatment,
        force_single_link: adFormik?.values?.format === 'SINGLE' ? true : false,
        template_url_spec: applink_treatment === 'web_only'
          ? template_url_spec?.map(({ adsAccountId, webUrl }) => ({
              adsAccountId,
              webUrl,
            }))
          : template_url_spec?.map(({ override_catalog, ...rest }) => rest),
        ...(urlTags.length > 0 && { url_tags: urlTags }),
        adcreatives: adcreatives,
        adsAccounts: adsAccounts,
      };

      const payload = removeEmptyValues(intialPayload);

      try {

        createAdspressoAds(payload).unwrap()
        .then(response => {
            toast({
            position: "top-right",
            title: "Ad creative added",
            status: "success",
            duration: 4000,
            isClosable: true,
            });
            // console.log('Ads creative created successfully:', response);
        })
        .catch(error => {
            const errorMessage = error?.message || "An unexpected error occurred";

            toast({
            position: "top-right",
            title: "Error creating Ad creative",
            description: errorMessage,
            status: "error",
            duration: 4000,
            isClosable: true,
            });
        });

      } catch {
      } finally {
        setSelectedAdAccounts([]);
        resetForm();
      }
    },
  });

  useEffect(() => {
    if (selectedAdAccounts.length > 0) {
      setExpandedUrlTagSections(prev => {
        // Only set default if nothing is expanded yet
        if (Object.keys(prev).length === 0) {
          return { [selectedAdAccounts[0].adsAccountId]: true };
        }
        return prev;
      });
    }
  }, [selectedAdAccounts]);
  

  const handleAdSetChange = (selectedValues) => {
    const updated = {};
    selectedAdAccounts?.forEach(account => {
      updated[account.adsAccountId] = selectedValues;
    });
    formik.setFieldValue('adSets', updated);
  };

  const handleUrlChange = (constructedUrl, adsAccountId) => {
  
    const existingLinks = formik.values.links || [];
  
    const updatedLinks = existingLinks.some(link => link.adsAccountId === adsAccountId)
      ? existingLinks.map(link =>
          link.adsAccountId === adsAccountId ? { ...link, link: constructedUrl } : link
        )
      : [...existingLinks, { adsAccountId, link: constructedUrl }];
  
    formik.setFieldValue('links', updatedLinks);
  };

  const handleUrlParamChange = (constructedUrl, adsAccountId) => {
    const existingLinks = formik.values.url_tags || [];
  
    const updatedLinks = existingLinks.some(url_tags => url_tags.adsAccountId === adsAccountId)
      ? existingLinks.map(url_tags =>
        url_tags.adsAccountId === adsAccountId ? { ...url_tags, url_tags: constructedUrl } : url_tags
        )
      : [...existingLinks, { adsAccountId, url_tags: constructedUrl }];
  
    formik.setFieldValue('url_tags', updatedLinks);
  };

  return (
    <>

    {/* Modal for "Add a frame" */}
      {isCreativeModalOpen && 
        <CatalogueFrame 
          isOpen={isCreativeModalOpen}
          onClose={onCreativeModalClose}
          productSetId={adsetsListData?.data[0]?.promoted_object[0]?.product_set_id}
          selectedAdAccounts={selectedAdAccounts.filter(acc => acc.adsAccountId === selectedAccountId)} 
          selectedAccountId={selectedAccountId}
          values={formik.values}
          setFieldValue={formik.setFieldValue}
        />
      }

      <Modal isOpen={isUrlModalOpen} onClose={() => setUrlModalOpen(false)} scrollBehavior={"inside"}
        size={"2xl"}
        isCentered={true}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <BuildUrlParams links={formik?.values?.links} onClose={() => setUrlModalOpen(false)} onUrlChange={handleUrlChange} selectedAccountId={selectedAccountId} />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isUrlParamModalOpen} onClose={() => setUrlParamModalOpen(false)} scrollBehavior={"inside"}
        size={"2xl"}
        isCentered={true}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader css={{ boxShadow: '0 0px 5px rgba(0, 0, 0, 0.1)' }}>Build a URL parameter</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UrlParameter urls={formik?.values?.url_tags} onClose={() => setUrlParamModalOpen(false)} onUrlChange={handleUrlParamChange} selectedAccountId={selectedAccountId} />
          </ModalBody>
        </ModalContent>
      </Modal>

      <Flex flex="3" direction="column" gap={2}>
        <Flex
            direction="column"
            mb={{ base: 3, md: 2 }}
            bg={backgroundColor}
            p={{ base: 3, md: 4 }}
            borderRadius="md"
            gap={{ base: 2, md: 3 }}
        >
            <Flex direction="column" >
              {/* Ad Account Select */}
              <Flex direction={'column'} gap={'10px'} mb={'10px'}>
                <Text fontSize={'14px'} fontWeight={'bold'}>Select Ad accounts</Text>
                <CheckboxGroup
                  colorScheme="brand"
                  value={selectedAdAccounts.map(account => account.adsAccountId)}
                  onChange={handleAdAccountChange}
                  isDisabled={userInfo?.role !== 'client'}
                >
                  {adsAccounts?.map(account => (
                    <Box key={account.adsAccountId} px={'5px'} py={'2px'}>
                      <Checkbox value={account.adsAccountId}>
                        <Text fontSize={'14px'} noOfLines={2} maxWidth={'510px'}>
                          {/* {account.adsAccountName.length > 20
                              ? `${account.adsAccountName.substring(0, 20)}...`
                              : account.adsAccountName} */}
                          {account.adsAccountName}
                        </Text>
                      </Checkbox>
                    </Box>
                  ))}
                </CheckboxGroup>
              </Flex>


              {/* Ad Set Select */}
              <Flex direction="column" gap={'5px'}>
                <Text fontSize={'14px'} fontWeight={'bold'}>Select Ad sets</Text>
                <CheckboxGroup
                  colorScheme="brand"
                  value={Array.from(new Set(Object.values(formik.values.adSets || {}).flat()))}
                  onChange={(e) => {
                    handleAdSetChange(e);
                  }}
                  isDisabled={!selectedAdAccounts.length > 0}
                >
                  {adsetsListData?.data?.map((adSet, index) => (
                    <Box key={`${adSet._id}-${index}`} px={'5px'} py={'2px'}>
                      <Checkbox value={adSet._id} display={'flex'} flex={1}>
                        <Text fontSize={'14px'} noOfLines={2} maxWidth={'510px'}>{adSet?.name}</Text>
                      </Checkbox>
                    </Box>
                  ))}
                </CheckboxGroup>

                {formik.touched.adSets && formik.errors.adSets && (
                  <Text fontSize="12px" color="red.500" mt={1}>
                    {formik.errors.adSets}
                  </Text>
                )}
              </Flex>
            </Flex>

            <Divider mt={'10px'} />

            <Flex direction="column" gap={1}>
                <Flex alignItems="center">
                    <Text fontWeight="bold">Destination</Text>
                </Flex>
                <Flex alignItems="center" gap={2}>
                    <Text fontWeight="bold" fontSize={'14px'}>Default destination</Text>
                    <Tooltip
                        borderRadius={5}
                        label={
                            <Box fontSize="sm" p="5px">
                                <Text>
                                    Send people to either your app or website URL.
                                </Text>
                            </Box>
                        }
                        fontSize="md">
                        <InfoOutlineIcon pointerEvents="all" />
                    </Tooltip>
                </Flex>
                <Select
                    name="applink_treatment"
                    bg="#fff"
                    size="sm"
                    borderRadius="md"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.applink_treatment}
                >
                    <option value="web_only">Website</option>
                    <option value="deeplink_with_web_fallback">App</option>
                </Select>
            </Flex>
                        
            {selectedAdAccounts.map((account, index) => {
                const accountId = account.adsAccountId;

                const linkObj = formik.values.links.find(l => l.adsAccountId === accountId) || {};
                const templateObj = formik.values.template_url_spec.find(t => t.adsAccountId === accountId) || {};

                const formikLinkIndex = formik.values.links.findIndex(l => l.adsAccountId === accountId);

                const linkTouched = formik.touched?.links?.[formikLinkIndex]?.link;
                const linkError = formik.errors?.links?.[formikLinkIndex]?.link;

                const templateIndex = formik.values.template_url_spec.findIndex(
                    t => t.adsAccountId === accountId
                );

                const iosUrlError = formik.errors?.template_url_spec?.[templateIndex]?.iosUrl;
                const iosUrlTouched = formik.touched?.template_url_spec?.[templateIndex]?.iosUrl;

                const androidUrlError = formik.errors?.template_url_spec?.[templateIndex]?.androidUrl;
                const androidUrlTouched = formik.touched?.template_url_spec?.[templateIndex]?.androidUrl;

                const webUrlError = formik.errors?.template_url_spec?.[templateIndex]?.webUrl;
                const webUrlTouched = formik.touched?.template_url_spec?.[templateIndex]?.webUrl;


                const updateLinkField = (field, value) => {
                  const updated = formik.values.links.map(item =>
                    item.adsAccountId === accountId ? { ...item, [field]: value } : item
                  );
                
                  if (!formik.values.links.some(item => item.adsAccountId === accountId)) {
                    updated.push({ adsAccountId: accountId, [field]: value });
                  }
                
                  formik.setFieldValue('links', updated);
                };  

              // Normalize IDs so string/number mismatches don't break findIndex
              const normalizeId = (v) => String(v);
              const currentId = normalizeId(accountId);

              const setTemplateField = (field, value) => {
                const list = formik.values.template_url_spec || [];
                const idx = list.findIndex(item => normalizeId(item.adsAccountId) === currentId);

                if (idx >= 0) {
                  // Update just that field in-place using a path (safer with concurrent updates)
                  formik.setFieldValue(`template_url_spec[${idx}].${field}`, value);
                } else {
                  // Insert a new row for this accountId with only the changed field
                  formik.setFieldValue('template_url_spec', [
                    ...list,
                    { adsAccountId: currentId, [field]: value },
                  ]);
                }
              };

              // field-specific updaters
              const updateAppId       = (v) => setTemplateField('app_id', v);
              const updateIosUrl      = (v) => setTemplateField('iosUrl', v);
              const updateAndroidUrl  = (v) => setTemplateField('androidUrl', v);
              const updateOverrideCat = (v) => setTemplateField('override_catalog', v);
              const updateWebUrl      = (v) => setTemplateField('webUrl', v);
                
                
            return (
                <Box key={accountId}>
                    <Flex alignItems="center" gap={2} mb={'5px'}>
                            <Text fontWeight="bold" fontSize="14px">
                            {truncateString(account.adsAccountName, 40)}
                            </Text>
                            <Text fontSize="12px">({account.adsAccountId})</Text>
                    </Flex>

                {/* Website URL */}
                <Flex direction={'column'}>
                    <Flex alignItems="center" mb={'5px'} >
                        <Text fontWeight="bold" fontSize="14px">
                        Website URL
                        </Text>
                        <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text>
                        <Tooltip
                        borderRadius={5}
                        label={
                            <Box fontSize="sm" p="5px">
                            <Text>Enter the URL for the webpage you want people to visit</Text>
                            </Box>
                        }
                        fontSize="md"
                        >
                        <InfoOutlineIcon pointerEvents="all" />
                        </Tooltip>
                    </Flex>
                    <Input
                        placeholder="http://www.example.com/page"
                        size="sm"
                        borderRadius={'md'}
                        value={linkObj.link || ''}
                        onChange={(e) => updateLinkField('link', e.target.value)}
                        onBlur={() => {
                            // Mark touched for the specific link field
                            if (formikLinkIndex >= 0) {
                              formik.setFieldTouched(`links[${formikLinkIndex}].link`, true);
                            }
                        }}
                    />
                    <Flex mt={1}>
                        <Button onClick={() => {
                            setUrlModalOpen(true)
                            setSelectedAccountId(account?.adsAccountId);
                        }}
                        variant="link"
                        color='brand'>
                        <Text fontSize={'12px'} mt={'5px'}>{'Build a URL parameter'}</Text>
                        </Button>
                    </Flex>
                    {linkTouched && linkError && (
                        <Text fontSize="12px" color="red.500" mt={1}>{linkError}</Text>
                    )}
                </Flex>

                {adFormik?.values?.format === 'CAROUSEL' ? 
                  <Flex direction="column" gap={1} mt={'10px'}>
                      <Flex alignItems="center" gap={2}>
                          <Text fontWeight="bold" fontSize={'14px'}>Display link</Text>
                          {/* <Text display="inline-flex" fontSize="18px" color="red.400" mr={2}>*</Text> */}
                          <Tooltip
                              borderRadius={5}
                              label={
                                  <Box fontSize="sm" p="5px">
                                      <Text>Show a short version of your website URL instead of your full URL. The domains for your website URL and display link should match.</Text>
                                  </Box>
                              }
                              fontSize="md"
                          >
                              <InfoOutlineIcon pointerEvents="all" />
                          </Tooltip>
                      </Flex>
                      <Input
                          placeholder="Enter link to show on last carousel card"
                          bg="#fff"
                          size='sm'
                          borderRadius="md"
                          value={linkObj.caption || ''}
                          onChange={(e) => updateLinkField('caption', e.target.value)}
                      />
                  </Flex>
                :null}

                {/* Deep links only if applink_treatment is set */}
                {formik.values.applink_treatment === 'deeplink_with_web_fallback' && (
                    <>

                    <Flex direction={'column'} gap={1} mt={'10px'}>
                        <Flex alignItems="center" gap={2}>
                            <Text fontWeight="bold" fontSize={'14px'}>Mobile app</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="sm" p="5px">
                                        <Text>Only select a mobile app if you are using a universal link (iOS or Android app link).</Text>
                                    </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>
                        <Select
                            size="sm"
                            borderRadius="md"
                            value={templateObj.app_id || ''}
                            onChange={(e) => updateAppId(e.target.value)}
                        >
                            <option value="">Choose app</option>
                            {advertisableApps
                                ?.find(app => app.adsAccountId === account.adsAccountId)
                                ?.apps
                                ?.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </Select>
                    </Flex> 

                    <Flex direction={'column'} gap={2} mt={'10px'}>
                        <Flex alignItems="center" gap={2}>
                            <Text fontWeight="bold" fontSize="14px">iOS deep link</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="14px" p="5px">
                                        <Text>
                                            Use a deep link to specify where in your app to send people. Entering an iOS deep link here will override any iOS deep links for items in the selected catalog.
                                        </Text>
                                    </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>
                        <CustomDeepLinkInput
                            value={templateObj.iosUrl || ''}
                            onChange={(url) => updateIosUrl(url)}
                            error={iosUrlTouched && iosUrlError}
                            placeholder={'Enter the deep link URL'}
                        />
                    </Flex>

                    <Flex direction={'column'} gap={2} mt={'10px'}>
                        <Flex alignItems="center" gap={2}>
                            <Text fontWeight="bold" fontSize="14px">Android deep link</Text>
                            <Tooltip
                                borderRadius={5}
                                label={
                                    <Box fontSize="14px" p="5px">
                                        <Text>
                                            Use a deep link to specify where in your app to send people. Entering an Android deep link here will override any Android deep links for items in the selected catalog.
                                        </Text>
                                    </Box>
                                }
                                fontSize="md"
                            >
                                <InfoOutlineIcon pointerEvents="all" />
                            </Tooltip>
                        </Flex>
                        <CustomDeepLinkInput
                            value={templateObj.androidUrl || ''}
                            onChange={(url) => updateAndroidUrl(url)}
                            error={androidUrlTouched && androidUrlError}
                            placeholder={'Enter the deep link URL'}
                        />
                    </Flex>
                    </>
                )}

                  <Flex direction="column" gap={2} mt={'10px'}>
                      <Checkbox
                          onChange={(e) => updateOverrideCat(e.target.checked)}
                          colorScheme="brand"
                          isChecked={templateObj.override_catalog}
                          alignItems="flex-start"
                      >
                          <Flex direction="column">
                          <Flex align="center" mt={'-3px'}>
                              <Text fontWeight="bold" fontSize="14px">
                              Override catalog website deep links&nbsp;
                              </Text>
                              <Tooltip
                              borderRadius={5}
                              label={
                                  <Box fontSize="sm" p="5px">
                                  <Text>
                                      Link each item in your catalog to a specific page on your website.
                                      This will override any deep links from the catalog feed.
                                  </Text>
                                  </Box>
                              }
                              fontSize="md"
                              >
                              <InfoOutlineIcon pointerEvents="all" />
                              </Tooltip>
                          </Flex>
                            <Text fontSize="11px">
                                This allows you to override website deep links for items in the catalog associated with this ad account.
                            </Text>
                          </Flex>
                      </Checkbox>

                      {templateObj.override_catalog && (
                          <>
                          <Flex alignItems="center" gap={2}>
                              <Text fontWeight="bold" fontSize="14px">Set catalog deep links</Text>
                              <Tooltip
                              borderRadius={5}
                              label={
                                  <Box fontSize="14px" p="5px">
                                  <Text>
                                      This deep link will override catalog item URLs for this ad account.
                                  </Text>
                                  </Box>
                              }
                              fontSize="md"
                              >
                              <InfoOutlineIcon pointerEvents="all" />
                              </Tooltip>
                          </Flex>

                          <CustomDeepLinkInput
                              value={templateObj.webUrl || ''}
                              onChange={(url) => updateWebUrl(url)}
                              error={webUrlTouched && webUrlError}
                              placeholder={'http://www.example.com/page'}
                          />
                          </>
                      )}
                  </Flex>
                </Box>
              );
            })}
        </Flex>

        <Flex
            direction="column"
            bg={backgroundColor}
            p={{ base: 3, md: 4 }}
            borderRadius="md"
            gap={{ base: 2, md: 3 }}
          >

          <Flex align="center" wrap="wrap">
            <Text fontWeight="bold">Ad creative&nbsp;</Text>
            <Tooltip
                borderRadius={5}
                label={
                <Box fontSize="sm" p="5px">
                    <Text>
                      Select and optimise your ad text, media and enhancements.
                    </Text>
                </Box>
                }
                fontSize="md"
            >
                <InfoOutlineIcon pointerEvents="all" />
            </Tooltip>
          </Flex>

          {adFormik?.values?.format === 'CAROUSEL' ?
          <>
            <Flex align="center" wrap="wrap" gap={2}>
              <Text fontWeight="bold" fontSize={'14px'}>Creative options</Text>
              <Tooltip
                  borderRadius={5}
                  label={
                  <Box fontSize="sm" p="5px">
                      <Text>
                        Choose a creative format for your carousel cards. The slideshow format will default to a single image if you don't provide more than one image per item.
                      </Text>
                  </Box>
                  }
                  fontSize="md"
              >
                  <InfoOutlineIcon pointerEvents="all" />
              </Tooltip>
            </Flex>
            <Flex>
                  <RadioGroup
                      name="format_option"
                      value={formik.values.format_option}
                      onChange={(value) => {
                        formik.setFieldValue('format_option', value);
                      }}
                    >
                      <Stack spacing={5} direction='column'>
                        <Flex direction="column">
                          <Flex align="center">
                            <Radio colorScheme='brand' value='carousel_images_multi_items' />
                            <Text fontWeight="bold" ml={2} fontSize={'14px'}>Single image</Text>
                          </Flex>
                          <Text fontSize={'12px'} ml={6}>Show one image per card for each item in your catalog.</Text>
                        </Flex>

                        <Flex direction="column">
                          <Flex align="center">
                            <Radio colorScheme='brand' value='carousel_slideshows' />
                            <Text fontWeight="bold" ml={2} fontSize={'14px'}>Slideshow </Text>
                          </Flex>
                          <Text fontSize={'12px'} ml={6}>Create a looping video for each card using multiple images per item.</Text>
                        </Flex>
                      </Stack>
                  </RadioGroup>
            </Flex>
          </>
        :null}


          <Flex align="center" wrap="wrap" gap={2}>
            <Text fontWeight="bold" fontSize={'14px'}>Creative tools</Text>
            <Tooltip
                borderRadius={5}
                label={
                <Box fontSize="sm" p="5px">
                    <Text>
                      Customise how images from your catalogue appear in your ads. Frame products and services with engaging graphics and add helpful info from your catalogue, such as price or free delivery.
                    </Text>
                </Box>
                }
                fontSize="md"
            >
                <InfoOutlineIcon pointerEvents="all" />
            </Tooltip>
          </Flex>

          {(formik.values.format_option === 'carousel_slideshows' && adFormik?.values?.format === 'CAROUSEL')  &&
            <Card 
                borderRadius="5px" 
                p={2}
                borderLeft={"4px solid"}
                borderColor={"gray.100"}
            >
              <CardBody as={Flex} align="flex-start" gap={2} p={1}>
                <InfoOutlineIcon style={{ pointerEvents: 'all' }} />
                <Text fontSize="12px">
                  Make sure you have multiple images for each item in your product set. When multiple images aren't available, a single image shows instead.
                </Text>
              </CardBody>
            </Card>
          }

          {(adFormik?.values?.format === 'SINGLE' || 
            (formik.values.format_option === 'carousel_images_multi_items' && adFormik?.values?.format === 'CAROUSEL')) && (
            <>
              {selectedAdAccounts.map((account) => {
                const accountId = account.adsAccountId;

                return (
                  <Flex key={accountId} direction="column" mb={2}>
                    {/* Account Header */}
                    <Flex align="center" justify="space-between">
                      <Flex direction="column">
                        <Text fontWeight="bold" fontSize="14px">
                          {truncateString(account.adsAccountName, 50)}
                        </Text>
                        <Text fontSize="12px">{accountId}</Text>
                      </Flex>

                      {/* Edit Creative Menu for this account */}
                      <Menu>
                        <MenuButton
                          as={Button}
                          variant="outline"
                          fontWeight="normal"
                          bg={inputBackground}
                          _active={{ background: inputBackground, borderColor: "blue.500" }}
                          _hover={{ background: inputBackground, borderColor: "gray.300" }}
                          rightIcon={<ChevronDownIcon boxSize={5} />}
                          size="sm"
                        >
                          Edit creative
                        </MenuButton>
                        <MenuList minW="135px" px={2} py={1}>
                          <MenuItem
                            fontSize="14px"
                            onClick={() => {
                              setSelectedAccountId(accountId);
                              onCreativeModalOpen();
                            }}
                          >
                            Add a frame
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Flex>
                  </Flex>
                );
              })}
            </>
          )}

          <Box direction="column" mb={4}>
              <Flex alignItems="center" mb={2}>
                  <Text fontWeight="bold" fontSize={'14px'} mr={2}>Primary text</Text>
                  <Tooltip
                  borderRadius={5}
                  label={
                      <Box fontSize="sm" p="5px">
                      <Text>
                          The primary text for your ad appears in most placements, though the position
                          varies. You can use @ to tag Facebook profiles or Pages. We recommend 125
                          characters or less.
                      </Text>
                      </Box>
                  }
                  fontSize="md">
                  <InfoOutlineIcon pointerEvents="all" />
                  </Tooltip>
              </Flex>
              <CustomDeepLinkInput
                  value={formik.values.message}
                  onChange={(message) => formik.setFieldValue('message', message)}
                  error={
                      formik.touched.message &&
                      formik.errors.message
                  }
                  placeholder={'Tell people what your ad is about'}
              />
          </Box>

          <Box direction="column" mb={4}>
            <Flex alignItems="center" mb={2} gap={2}>
              <Text fontWeight="bold" fontSize={'14px'}>Headline</Text>
              <Tooltip
                borderRadius={5}
                label={
                  <Box fontSize="sm" p="5px">
                    <Text>
                      Add up to five brief headlines to let people know what your ad is about. Each
                      headline can have a maximum of 255 characters. Headlines won't appear in all
                      placements.
                    </Text>
                  </Box>
                }
                fontSize="md">
                <InfoOutlineIcon pointerEvents="all" />
              </Tooltip>
            </Flex>
            <CustomDeepLinkInput
                  value={formik.values.name}
                  onChange={(name) => formik.setFieldValue('name', name)}
                  error={
                      formik.touched.name &&
                      formik.errors.name
                  }
                  placeholder={'Add an attention-grabbing headline'}
            />
          </Box>

          <Box direction="column" mb={'10px'}>
            <Flex alignItems="center" mb={2} gap={2}>
              <Text fontWeight="bold" fontSize="14px">Call to action</Text>
              <Tooltip
                borderRadius={5}
                label={
                  <Box fontSize="sm" p="5px">
                    <Text>This is the only available option.</Text>
                  </Box>
                }
                fontSize="md"
              >
                <InfoOutlineIcon pointerEvents="all" />
              </Tooltip>
            </Flex>
            <Select
              name="type"
              borderRadius="md"
              value={formik.values.type}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              size="sm"
            >
              {call_to_action_options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Box>
        </Flex>

        <Box bg={backgroundColor} p={{ base: 3, md: 4 }} borderRadius="md">
          {selectedAdAccounts.map((account, index) => {
            const accountId = account.adsAccountId;
            const urlTagsObj = formik.values.url_tags.find(l => l.adsAccountId === accountId) || {};

            const updateUrlTagsValue = (value) => {
              const updated = formik.values.url_tags.map(item =>
                item.adsAccountId === accountId ? { ...item, url_tags: value } : item
              );

              if (!formik.values.url_tags.some(item => item.adsAccountId === accountId)) {
                updated.push({ adsAccountId: accountId, url_tags: value });
              }

              formik.setFieldValue('url_tags', updated);
            };

            return (
              <Flex key={`${account.adsAccountId}-${index}`} direction="column" mb={4} border={'1px solid'} borderColor={'gray.200'} borderRadius={'md'}>
                <Flex
                  alignItems="center"
                  justifyContent="space-between"
                  onClick={() => {
                    setExpandedUrlTagSections(prev => ({
                      ...prev,
                      [account.adsAccountId]: !prev[account.adsAccountId],
                    }));
                  }}
                  cursor="pointer"
                  p={2}
                >
                  <Flex alignItems="center" gap={2}>
                    <Text fontWeight="bold" fontSize="14px">
                      {truncateString(account.adsAccountName, 40)}
                    </Text>
                    <Text fontSize="12px" color="gray.600">({account.adsAccountId})</Text>
                  </Flex>
                  <Box>
                    <ChevronDownIcon
                      boxSize={5}
                      transform={expandedUrlTagSections[accountId] ? 'scaleY(-1)' : 'scaleY(1)'}
                      transition="transform 0.1s ease-in-out"
                    />
                  </Box>
                </Flex>

                <Collapse in={expandedUrlTagSections[accountId]} animateOpacity>
                  <Box p={2}>
                    <Flex alignItems={'center'}>
                      <Text mb={2} fontWeight="bold" fontSize="14px">
                        URL parameters{" "}
                        <Text as="span" fontWeight="bold" color={'gray.600'}>Optional</Text>
                      </Text>
                      <Tooltip
                        borderRadius={5}
                        label={
                          <Box fontSize="sm" p={'5px'}>
                            <Text>
                              Add parameters to the end of your website URL to track where your visitors are coming from.
                            </Text>
                          </Box>
                        }
                        fontSize="md"
                      >
                        <InfoOutlineIcon pointerEvents="all" ml={2} mb={2} />
                      </Tooltip>
                    </Flex>

                    <Input
                      type="url"
                      bg="#fff"
                      borderRadius="md"
                      placeholder="key1=value1&key2=value2"
                      value={urlTagsObj.url_tags || ''}
                      onChange={(e) => updateUrlTagsValue(e.target.value)}
                      size="sm"
                    />

                    <Flex mt={1} mb={2}>
                      <Button
                        onClick={() => {
                          setUrlParamModalOpen(true);
                          setSelectedAccountId(account?.adsAccountId);
                        }}
                        variant="link"
                        color='brand'
                      >
                        <Text fontSize={'12px'} mt={'5px'}>{'Build a URL parameter'}</Text>
                      </Button>
                    </Flex>
                  </Box>
                </Collapse>
              </Flex>
            );
          })}
        </Box>

        {/* Save Button */}
        <Flex justifyContent="flex-start" mt={'10px'}>
          <Button
            colorScheme="brand"
            onClick={() => formik.handleSubmit()}
            isLoading={isAdsCreating}
            size={'sm'}
            isDisabled={
              selectedAdAccounts.length === 0 
            }            
          >
            Save
          </Button>
        </Flex>
      </Flex>
    </>
  );
}

export default CatalogueMediaCreative;

